import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { finalize, startWith, takeUntil, tap } from 'rxjs';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';

import { PrivateSegmentsDrawerData } from './group-custom-private-segments-drawer.helper';
import { fadeInGrowEnter, fadeOutLeave } from '@libs/src/animations/animations';
import {
  DefaultInteractionSettings,
  Group,
  GroupHiddenCustomSegment,
} from '@libs/src/models/group.model';

import { GroupCustomSegmentsService } from '../../group-custom-segments-settings/group-custom-segments.service';

interface GroupCustomPrivateSegmentFormGroup {
  defaultInteractions: FormControl<DefaultInteractionSettings>;
  privacySettings: FormGroup<PrivacySettingsFormGroup>;
  segmentInput: FormControl<{
    description: string;
    title: string;
  }>;
}

interface PrivacySettingsFormGroup {
  editable_by_self: FormControl<boolean>;
  viewable_by_self: FormControl<boolean>;
  visible_in_onboarding: FormControl<boolean>;
}

@Component({
  animations: [fadeInGrowEnter, fadeOutLeave],
  providers: [DestroySubscriptionsService, GroupCustomSegmentsService],
  selector: 'app-group-custom-private-segments-drawer',
  templateUrl: './group-custom-private-segments-drawer.component.html',
})
export class GroupCustomPrivateSegmentsDrawerComponent implements OnInit {
  @Input() data: PrivateSegmentsDrawerData;
  group: Group;
  isLoading: boolean;
  privateSegment: GroupHiddenCustomSegment;
  privateSegmentForm: FormGroup<GroupCustomPrivateSegmentFormGroup>;

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    private readonly fb: FormBuilder,
    private readonly groupsBackend: GroupsBackend,
    private readonly drawerRef: NzDrawerRef,
    private readonly groupCustomSegmentsService: GroupCustomSegmentsService,
  ) {}

  get defaultInteractionsControl() {
    return this.privateSegmentForm.get('defaultInteractions') as FormControl;
  }

  get segmentInputControl() {
    return this.privateSegmentForm.get('segmentInput') as FormControl;
  }

  get privacySettingsFormGroup() {
    return this.privateSegmentForm.controls
      .privacySettings as FormGroup<PrivacySettingsFormGroup>;
  }

  get viewableBySelfControl() {
    return this.privacySettingsFormGroup.get(
      'viewable_by_self',
    ) as FormControl<boolean>;
  }

  get editableBySelfControl() {
    return this.privacySettingsFormGroup.get(
      'editable_by_self',
    ) as FormControl<boolean>;
  }

  get showInOnboardingControl() {
    return this.privacySettingsFormGroup.get(
      'visible_in_onboarding',
    ) as FormControl<boolean>;
  }

  ngOnInit(): void {
    const { group, privateSegment = {}, customSegmentName = null } = this.data;
    this.privateSegment = privateSegment;
    this.group = group;
    const { editable_by_self, visible_in_onboarding, viewable_by_self } =
      privateSegment.privacy_settings || {};
    this.privateSegmentForm = this.fb.group<GroupCustomPrivateSegmentFormGroup>(
      {
        defaultInteractions: new FormControl(),
        privacySettings: this.fb.group({
          editable_by_self: new FormControl(editable_by_self ?? false),
          viewable_by_self: new FormControl(viewable_by_self ?? false),
          visible_in_onboarding: new FormControl(
            visible_in_onboarding ?? false,
          ),
        }),
        segmentInput: new FormControl({
          description: privateSegment?.description ?? '',
          title: customSegmentName ?? privateSegment?.title ?? '',
        }),
      },
    );
    this.viewableBySelfControl.valueChanges
      .pipe(
        startWith(
          this.privateSegment?.privacy_settings?.viewable_by_self || false,
        ),
        tap((value) => {
          if (!value) {
            this.editableBySelfControl.disable();
            this.editableBySelfControl.reset();
            this.showInOnboardingControl.disable();
            this.showInOnboardingControl.reset();
            return;
          }
          this.editableBySelfControl.enable();
          this.showInOnboardingControl.enable();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  submitForm() {
    if (!this.privateSegmentForm.valid) {
      return;
    }
    this.isLoading = true;
    const { is_selectable_for_user_introduction_goals, selection_type } =
      this.defaultInteractionsControl.value;
    const { description, title } = this.segmentInputControl.value;
    const { editable_by_self, viewable_by_self, visible_in_onboarding } =
      this.privacySettingsFormGroup.value;
    const formValue: GroupHiddenCustomSegment = {
      description,
      is_selectable_for_user_introduction_goals,
      privacy_settings: {
        editable_by_self: editable_by_self ?? false,
        viewable_by_self: viewable_by_self ?? false,
        visible_in_onboarding: visible_in_onboarding ?? false,
      },
      title: title.trim(),
    };
    const customSegmentObservable =
      this.privateSegment && this.privateSegment._id
        ? this.groupsBackend.patchHiddenSegments(
            this.data.group,
            this.privateSegment,
            formValue,
          )
        : this.groupsBackend.postHiddenSegments(this.data.group, {
            ...formValue,
            selection_type,
          });
    customSegmentObservable
      .pipe(
        tap((response: GroupHiddenCustomSegment[]) => {
          this.closeDrawer(response);
        }),
        this.groupCustomSegmentsService.successHandler(
          this.group,
          'hidden_custom_segments',
        ),
        this.groupCustomSegmentsService.errorCatcher(),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  closeDrawer(response?: GroupHiddenCustomSegment[]) {
    this.drawerRef.close(response);
  }
}
